

// export default DedicatedAccessSection;
// src/components/admin_and_user/DedicatedAccessSection.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { message, Modal } from "antd";
import courseService from "../../services/course.service";
import PaymentWrapper from "../payment/PaymentWrapper";

/* ============ Couleurs logo ============ */
const C = {
  blue: "#0054A6",
  blueDark: "#003B75",
  blueLight: "#e8f1fb",
  orange: "#E8860C",
  orangeLight: "#FF8C00",
  orangeBg: "#fff8f0",
  text: "#333",
  textLight: "#888",
  border: "#eaedf2",
  bg: "#f7f8fa",
};

/* ---------------- Modal avec focus one-shot ---------------- */
const FocusModal = React.memo(function FocusModal({
  visible, onClose, title, children, initialFocusRef,
}) {
  const boxRef = useRef(null);
  const prevVisible = useRef(false);
  const hasFocused = useRef(false);

  useEffect(() => {
    const justOpened = !prevVisible.current && visible;
    if (justOpened && !hasFocused.current) {
      if (initialFocusRef?.current) initialFocusRef.current.focus();
      else if (boxRef.current && !boxRef.current.contains(document.activeElement)) {
        const el = boxRef.current.querySelector("input, select, textarea, button, a");
        el?.focus();
      }
      hasFocused.current = true;
    }
    prevVisible.current = visible;
    return () => {
      if (!visible) { hasFocused.current = false; prevVisible.current = false; }
    };
  }, [visible, initialFocusRef]);

  if (!visible) return null;

  return (
    <div role="dialog" aria-modal="true" style={styles.modalOverlay}
      onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div ref={boxRef} style={styles.modalCard}>
        {title ? <h3 style={styles.modalTitle}>{title}</h3> : null}
        {children}
      </div>
    </div>
  );
});

/* ---------------- Formulaire Service commercial ---------------- */
function ContactDialog({
  visible, onClose, selectedPlan, contactForm, setContactForm,
  contactErrors, setContactErrors, contactLoading, contactSuccess,
  setContactSuccess, onSubmit,
}) {
  const firstNameRef = useRef(null);
  const update = (k, v) => setContactForm((p) => ({ ...p, [k]: v }));
  const field = useMemo(() => ({
    label: { display: "block", fontSize: 12, marginBottom: 6, color: "#333" },
    input: { width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13 },
    error: { color: "#d9363e", fontSize: 11, marginTop: 4 },
  }), []);

  return (
    <FocusModal visible={visible} onClose={onClose} title="Contacter le service commercial" initialFocusRef={firstNameRef}>
      {contactSuccess && (<div style={styles.contactSuccess}>✅ Merci ! Votre demande a été envoyée.</div>)}
      <div style={{ display: "grid", gap: 10 }}>
        <div>
          <label style={field.label}>Prénom <span style={{ color: "#d9363e" }}>*</span></label>
          <input ref={firstNameRef} style={field.input} value={contactForm.firstName}
            onChange={(e) => update("firstName", e.target.value)} placeholder="Votre prénom" />
          {contactErrors.firstName && <div style={field.error}>{contactErrors.firstName}</div>}
        </div>
        <div>
          <label style={field.label}>Nom de famille <span style={{ color: "#d9363e" }}>*</span></label>
          <input style={field.input} value={contactForm.lastName}
            onChange={(e) => update("lastName", e.target.value)} placeholder="Votre nom" />
          {contactErrors.lastName && <div style={field.error}>{contactErrors.lastName}</div>}
        </div>
        <div>
          <label style={field.label}>Courriel professionnel <span style={{ color: "#d9363e" }}>*</span></label>
          <input type="email" style={field.input} value={contactForm.email}
            onChange={(e) => update("email", e.target.value)} placeholder="prenom.nom@entreprise.com" />
          {contactErrors.email && <div style={field.error}>{contactErrors.email}</div>}
        </div>
        <div>
          <label style={field.label}>Nom de l'entreprise <span style={{ color: "#d9363e" }}>*</span></label>
          <input style={field.input} value={contactForm.company}
            onChange={(e) => update("company", e.target.value)} placeholder="Ex. MATU Assurances" />
          {contactErrors.company && <div style={field.error}>{contactErrors.company}</div>}
        </div>
        <div>
          <label style={field.label}>Type de demande <span style={{ color: "#d9363e" }}>*</span></label>
          <select style={field.input} value={contactForm.requestType}
            onChange={(e) => update("requestType", e.target.value)}>
            <option value="">Veuillez sélectionner</option>
            <option value="info">Demande d'informations</option>
            <option value="devis">Demande de devis</option>
            <option value="demo">Demander une démo</option>
            <option value="autre">Autre</option>
          </select>
          {contactErrors.requestType && <div style={field.error}>{contactErrors.requestType}</div>}
        </div>
        <div>
          <label style={field.label}>Message (optionnel)</label>
          <textarea style={{ ...field.input, minHeight: 80, resize: "vertical" }}
            value={contactForm.message} onChange={(e) => update("message", e.target.value)}
            placeholder="Décrivez brièvement votre besoin…" />
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
          <button onClick={onClose} style={styles.btnGrey}>Annuler</button>
          <button onClick={onSubmit} disabled={contactLoading} style={styles.btnPrimary}>
            {contactLoading ? "Envoi…" : "Soumettre"}
          </button>
        </div>
      </div>
    </FocusModal>
  );
}

/* ================================================================
   Composant principal — V3 White + Blue/Orange accents
   ================================================================ */
const DedicatedAccessSection = () => {
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const PERIODS = [
    { key: 'monthly', label: 'Mensuel' },
    { key: 'quarterly', label: 'Trimestriel' },
    { key: 'semiannual', label: 'Semestriel' },
    { key: 'annual', label: 'Annuel' },
  ];
  const MULTIPLIER = { monthly: 1, quarterly: 3, semiannual: 6, annual: 12 };
  const LABEL = { monthly: 'mois', quarterly: 'trimestre', semiannual: 'semestre', annual: 'an' };

  const computePrice = (plan, period) => {
    const monthly = Number(plan.price);
    const m = MULTIPLIER[period];
    const undiscountedAnnual = monthly * 12;
    let total = monthly * m;
    if (period === 'annual') {
      const rate = Number(plan.annualDiscountRate ?? 0);
      total = undiscountedAnnual * (1 - rate);
    }
    return { total, unitLabel: LABEL[period], undiscountedAnnual };
  };

  const normalizeCurrency = (cur) => {
    if (!cur) return 'USD';
    const c = String(cur).trim().toUpperCase();
    if (c === '$') return 'USD';
    if (c === '€') return 'EUR';
    if (['DH', 'د.م.', 'د.م', 'MAD'].includes(c)) return 'MAD';
    return /^[A-Z]{3}$/.test(c) ? c : 'USD';
  };

  const safeFmt = (v, currency) => {
    const n = Number(v || 0);
    const cur = String(currency || '').trim();
    if (/^[A-Z]{3}$/.test(normalizeCurrency(cur))) {
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: normalizeCurrency(cur) }).format(n);
    }
    const sym = cur || '$';
    return `${sym}${new Intl.NumberFormat('fr-FR').format(n)}`;
  };

  const PLAN_COLORS = [C.blue, "#00875A", C.orange, "#7C3AED", "#DC2626", "#0891B2"];
  const getColor = (plan, i) => plan.color || PLAN_COLORS[i % PLAN_COLORS.length];

  // Plans du backend
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [errorPlans, setErrorPlans] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await courseService.getDisplayedPlans(); setPlans(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setErrorPlans("Impossible de charger les offres.");
      } finally {
        setLoadingPlans(false);
      }
    })();
  }, []);

  // Achat
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [payingPlanId, setPayingPlanId] = useState(null);

  const handleBuyPlan = (plan) => {
    // 🔐 Vérifier si l'utilisateur est connecté
    const user = JSON.parse(localStorage.getItem('user') || 'null');

    if (!user || !user.id) {
      // Sauvegarder le plan choisi pour redirection après login
      sessionStorage.setItem('pendingPlan', JSON.stringify({ planId: plan.id }));
      message.info("🔒 Connectez-vous pour acheter cet accès SAP");
      // Petite pause pour que l'utilisateur voie le message
      setTimeout(() => {
        window.location.href = '/login';
      }, 800);
      return;
    }

    // ✅ Utilisateur connecté → procéder normalement
    setSelectedPlan(plan);
    setConfirmModalVisible(true);
    setPayingPlanId(plan?.id || null);
  };

  // Paiement
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [planToPayFor, setPlanToPayFor] = useState(null);

  const handleConfirmPurchase = () => {
    if (!selectedPlan) return;
    const { total } = computePrice(selectedPlan, billingPeriod);
    const normalizedCourse = {
      id: selectedPlan.id,
      title: `${selectedPlan.title} — ${LABEL[billingPeriod]}`,
      latest_price: Number(total.toFixed(2)),
      price: Number(total.toFixed(2)),
      currency: selectedPlan.currency || "USD",
      metadata: {
        billingPeriod,
        annualDiscountRate: Number(selectedPlan.annualDiscountRate ?? 0),
      },
    };
    setConfirmModalVisible(false);
    setPlanToPayFor(normalizedCourse);
    setPaymentModalVisible(true);
  };

  const handlePaymentSuccess = (result) => {
    const { paymentIntent, ticket, moneris } = result || {};
    if (paymentIntent) message.success(`🎉 Paiement réussi !`);
    else if (ticket || moneris) message.success("🎉 Paiement Moneris initialisé.");
    else message.success("🎉 Paiement terminé.");
    setPaymentModalVisible(false);
    setPlanToPayFor(null);
    setSelectedPlan(null);
    setPayingPlanId(null);
  };
  const handlePaymentError = (err) => { console.error(err); message.error(err?.message || "Erreur lors du paiement."); };
  const handlePaymentCancel = () => { setPaymentModalVisible(false); setPlanToPayFor(null); };

  // Contact
  const EMPTY_CONTACT = { firstName: "", lastName: "", email: "", company: "", requestType: "", message: "", captchaChecked: false };
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT);
  const [contactErrors, setContactErrors] = useState({});
  const [contactModalVisible, setContactModalVisible] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  const validateContact = () => {
    const e = {};
    if (!contactForm.firstName.trim()) e.firstName = "Champ requis";
    if (!contactForm.lastName.trim()) e.lastName = "Champ requis";
    if (!contactForm.email.trim()) e.email = "Champ requis";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email)) e.email = "Email invalide";
    if (!contactForm.company.trim()) e.company = "Champ requis";
    if (!contactForm.requestType) e.requestType = "Sélectionnez un type";
    setContactErrors(e);
    return Object.keys(e).length === 0;
  };

  const submitContact = async () => {
    if (!validateContact()) return;
    setContactLoading(true);
    setContactSuccess(false);
    try {
      await courseService.createSalesRequest({
        ...contactForm,
        planId: selectedPlan?.id || null,
        planTitle: selectedPlan?.title || null,
      });
      setContactSuccess(true);
      setContactForm(EMPTY_CONTACT);
      setContactErrors({});
    } catch (e) {
      console.error(e);
      message.error("Erreur lors de l'envoi. Réessayez.");
    } finally {
      setContactLoading(false);
    }
  };

  /* ==== RENDER ==== */
  return (
    <div style={styles.container}>
      {/* Responsive CSS for cards grid */}
      <style>{`
    /* ⭐ Animation pour les cartes */
    @keyframes plan-fade-in {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
    }

    .plan-card {
        animation: plan-fade-in 0.5s ease-out backwards;
        position: relative;
        cursor: default;
    }
    
    .plan-card:nth-child(1) { animation-delay: 0.05s; }
    .plan-card:nth-child(2) { animation-delay: 0.1s; }
    .plan-card:nth-child(3) { animation-delay: 0.15s; }
    .plan-card:nth-child(4) { animation-delay: 0.2s; }
    .plan-card:nth-child(5) { animation-delay: 0.25s; }

    .plan-card:hover {
        transform: translateY(-8px);
        box-shadow: 0 20px 40px rgba(0, 84, 166, 0.18) !important;
    }

    .plan-card:hover .plan-buy-btn {
        transform: scale(1.02);
        box-shadow: 0 6px 20px rgba(0,0,0,0.15) !important;
    }

    /* ⭐ Grille responsive intelligente */
    @media (max-width: 1200px) {
      .plans-grid { 
          grid-template-columns: repeat(3, 1fr) !important;
          max-width: 1000px !important;
      }
    }
    @media (max-width: 900px) {
      .plans-grid { 
          grid-template-columns: repeat(2, 1fr) !important;
          max-width: 700px !important;
      }
    }
    @media (max-width: 600px) {
      .plans-grid { 
          grid-template-columns: 1fr !important;
          max-width: 400px !important;
      }
    }
`}</style>
      {/* Header */}
      <div style={styles.header}>
        <span style={{
          fontSize: 11, fontWeight: 700, color: C.orange, letterSpacing: 1.5,
          textTransform: "uppercase", display: "inline-block",
          padding: "3px 12px", borderRadius: 12, background: C.orangeBg, marginBottom: 8,
        }}>
          Accès SAP dédiés
        </span>
        <h2 style={styles.title}>Accès partagé <span style={{ color: C.orange }}>SAP S/4 HANA</span></h2>
        <p style={styles.subtitle}>
          Inscrivez-vous pour bénéficier d'un accès SAP dédié, réservé à vous seul.
        </p>
        {/* ⭐ NOUVEAU : Message d'info pour l'authentification */}
        <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          background: `${C.blue}10`,
          border: `1px solid ${C.blue}33`,
          padding: "10px 18px",
          borderRadius: 30,
          marginTop: 14,
          fontSize: 13,
          color: C.blueDark,
          fontWeight: 500,
        }}>
          <span style={{ fontSize: 16 }}>🔒</span>
          <span>
            <strong>Compte requis</strong> pour le paiement —{" "}
            <a
              href="/login"
              style={{
                color: C.orange,
                fontWeight: 700,
                textDecoration: "none",
                borderBottom: `2px solid ${C.orange}`,
              }}
              onMouseEnter={e => e.target.style.color = C.blue}
              onMouseLeave={e => e.target.style.color = C.orange}
            >
              Connectez-vous
            </a>
            {" ou "}
            <a
              href="/register"
              style={{
                color: C.orange,
                fontWeight: 700,
                textDecoration: "none",
                borderBottom: `2px solid ${C.orange}`,
              }}
              onMouseEnter={e => e.target.style.color = C.blue}
              onMouseLeave={e => e.target.style.color = C.orange}
            >
              créez un compte
            </a>
          </span>
        </div>
        {/* Billing toggle */}
        <div style={{
          display: "inline-flex", gap: 2, padding: 3, borderRadius: 8,
          background: C.bg, border: `1px solid ${C.border}`, marginTop: 14,
          flexWrap: "wrap", justifyContent: "center",
        }}>
          {PERIODS.map(p => (
            <button key={p.key} onClick={() => setBillingPeriod(p.key)} style={{
              padding: "7px 16px", borderRadius: 6, border: "none", cursor: "pointer",
              fontFamily: "inherit",
              background: billingPeriod === p.key ? C.blue : "transparent",
              color: billingPeriod === p.key ? "white" : C.textLight,
              fontWeight: 600, fontSize: 12, transition: "all .2s",
            }}>
              {p.label}
              {p.key === "annual" && (
                <span style={{
                  marginLeft: 4, padding: "1px 6px", borderRadius: 8,
                  background: billingPeriod === p.key ? "rgba(255,255,255,0.2)" : C.orangeBg,
                  color: billingPeriod === p.key ? "white" : C.orange,
                  fontSize: 10, fontWeight: 700,
                }}>-20%</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Loading / Error */}
      {loadingPlans && <div style={{ color: C.textLight, textAlign: "center", padding: 24, fontSize: 13 }}>Chargement…</div>}
      {!loadingPlans && errorPlans && <div style={{ color: "#ef4444", textAlign: "center", padding: 24, fontSize: 13 }}>{errorPlans}</div>}

      {/* Plans Grid — PLEINE LARGEUR */}
      {!loadingPlans && !errorPlans && (
        <div className="plans-grid" style={{
          ...styles.cardsGrid,
          gridTemplateColumns: `repeat(${Math.min(plans.slice(0, 5).length, 5)}, 1fr)`,
        }}>          {plans.slice(0, 5).map((plan, i) => {
          const { total, unitLabel, undiscountedAnnual } = computePrice(plan, billingPeriod);
          const rate = Number(plan.annualDiscountRate ?? 0);
          const savings = Math.max(0, undiscountedAnnual - total);
          const isPriceZero = total === 0 || Number(plan.price) === 0;
          const c = getColor(plan, i);
          const isPop = i === 1;

          return (
            <div
              key={plan.id}
              className="plan-card"
              onClick={() => !isPriceZero && handleBuyPlan(plan)}
              style={{
                ...styles.card,
                border: isPop ? `2px solid ${c}` : `1px solid ${C.border}`,
                boxShadow: isPop ? `0 8px 28px ${c}30` : "0 4px 12px rgba(0,0,0,0.06)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                cursor: !isPriceZero ? "pointer" : "default",
              }}
            >
              {/* NEW badge */}
              {plan.isNew && (
                <div style={{
                  position: "absolute", top: 14, right: -26, background: "#ff4d4f",
                  color: "white", padding: "2px 32px", transform: "rotate(45deg)",
                  fontSize: 9, fontWeight: 700, zIndex: 1,
                }}>NEW</div>
              )}

              {/* Popular badge */}
              {isPop && (
                <div style={{
                  background: `linear-gradient(135deg, ${c}, ${c}cc)`, color: "white",
                  textAlign: "center", padding: "5px 0", fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                }}>⭐ POPULAIRE</div>
              )}

              {/* Card header — bande de couleur */}
              <div style={{
                padding: "20px 14px", textAlign: "center",
                background: `linear-gradient(135deg, ${c}, ${c}dd)`,
                position: "relative",
                overflow: "hidden",
              }}>
                {/* Effet de lumière en arrière-plan */}
                <div style={{
                  position: "absolute",
                  top: -30, right: -30,
                  width: 100, height: 100,
                  background: "rgba(255,255,255,0.1)",
                  borderRadius: "50%",
                }} />
                <div style={{
                  position: "absolute",
                  bottom: -40, left: -40,
                  width: 120, height: 120,
                  background: "rgba(0,0,0,0.05)",
                  borderRadius: "50%",
                }} />

                <div style={{
                  width: 48, height: 48, margin: "0 auto 10px",
                  backgroundColor: "rgba(255,255,255,0.25)",
                  borderRadius: 12,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  position: "relative",
                  zIndex: 1,
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(255,255,255,0.3)",
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" />
                  </svg>
                </div>
                <div style={{
                  color: "white",
                  fontSize: 16,        // 🔥 13 → 16
                  lineHeight: 1.4,     // 🔥 1.3 → 1.4
                  fontWeight: 700,
                  position: "relative",
                  zIndex: 1,
                  textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                }}>
                  {plan.title}
                </div>
              </div>

              {/* Card body */}
              {/* Card body */}
              <div style={{
                padding: "20px 16px", flex: 1, display: "flex",          // 🔥 14px 12px → 20px 16px
                flexDirection: "column", alignItems: "center", background: "white",
              }}>
                {/* Prix */}
                {!isPriceZero && (
                  <div style={{ textAlign: "center", marginBottom: 10 }}>
                    <span style={{ fontWeight: 800, fontSize: 28, color: c }}>   {/* 🔥 22 → 28 */}
                      {safeFmt(total, plan.currency)}
                    </span>
                    <span style={{ fontSize: 15, color: C.textLight, marginLeft: 4 }}>/{unitLabel}</span>   {/* 🔥 13 → 15 */}
                    {billingPeriod === 'annual' && rate > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <span style={{
                          fontSize: 10, padding: "2px 8px", borderRadius: 10,
                          background: `${c}10`, color: c, fontWeight: 600,
                        }}>
                          -{Math.round(rate * 100)}% · Éco. {safeFmt(savings, plan.currency)}/an
                        </span>
                        <div style={{ fontSize: 11, color: "#ccc", marginTop: 3 }}>
                          <s>{safeFmt(undiscountedAnnual, plan.currency)}/an</s>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Buy button */}
                {!isPriceZero && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();  // ⭐ Empêcher le double-déclenchement
                      handleBuyPlan(plan);
                    }}
                    disabled={payingPlanId === plan.id}
                    className="plan-buy-btn"
                    style={{
                      width: "100%", padding: "14px 0", borderRadius: 10, border: "none",
                      background: `linear-gradient(135deg, ${c}, ${c}cc)`, color: "white",
                      fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: "inherit",
                      boxShadow: `0 4px 14px ${c}40`, marginBottom: 12,
                      transition: "all 0.3s ease",
                      letterSpacing: 0.3,
                    }}
                  >
                    {payingPlanId === plan.id ? "Traitement…" : "💳 Paiement en ligne"}
                  </button>
                )}

                {/* Phone */}
                {plan.phone && (
                  <a
                    href={`tel:${plan.phone}`}
                    onClick={(e) => e.stopPropagation()}  // ⭐ Empêcher le déclenchement
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      marginBottom: 14, color: C.textLight, fontSize: 14, textDecoration: "none",
                      fontWeight: 500,
                    }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 15.18C15.69 14.9 16.08 14.82 16.43 14.93C17.55 15.3 18.75 15.5 20 15.5C20.55 15.5 21 15.95 21 16.5V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.25 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z" />
                    </svg>
                    {plan.phone}
                  </a>
                )}

                {/* Features */}
                {plan.features?.length > 0 && (
                  <div style={{
                    width: "100%", textAlign: "left", padding: 14,           // 🔥 10 → 14
                    background: C.bg, borderRadius: 8, flex: 1, border: `1px solid ${C.border}`,
                  }}>
                    <div style={{ fontWeight: 700, color: C.blueDark, marginBottom: 10, fontSize: 13 }}>   {/* 🔥 10.5 → 13, marginBottom 6 → 10 */}
                      Description de l'offre :
                    </div>
                    {plan.features.map((feature, j) => (
                      <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>   {/* 🔥 5 → 8, 4 → 8 */}
                        <svg width="14" height="14" viewBox="0 0 24 24" fill={c} style={{ flexShrink: 0, marginTop: 3 }}>   {/* 🔥 11 → 14 */}
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                        </svg>
                        <span style={{ fontSize: 13, color: "#444", lineHeight: 1.5 }}>{feature}</span>   {/* 🔥 11 → 13, #555 → #444 */}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        </div>
      )}

      {/* Bouton voir plus */}
      {!loadingPlans && !errorPlans && plans.length > 5 && (
        <div style={{ textAlign: "center", marginTop: 24 }}>
          <a href="/sap-access" style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "12px 32px", borderRadius: 10, textDecoration: "none",
            background: `linear-gradient(135deg, ${C.blue}, ${C.blueDark})`,
            color: "white", fontWeight: 700, fontSize: 14, fontFamily: "inherit",
            boxShadow: `0 4px 16px ${C.blue}22`, transition: "all 0.3s",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 24px ${C.blue}33`; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = `0 4px 16px ${C.blue}22`; }}
          >
            Voir tous les accès SAP
            <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" /></svg>
          </a>
          <p style={{ fontSize: 12, color: C.textLight, marginTop: 8 }}>
            {plans.length - 5} autre{plans.length - 5 > 1 ? "s" : ""} offre{plans.length - 5 > 1 ? "s" : ""} disponible{plans.length - 5 > 1 ? "s" : ""}
          </p>
        </div>
      )}

      {/* ==== MODALS ==== */}
      <FocusModal
        visible={confirmModalVisible}
        onClose={() => { setConfirmModalVisible(false); setSelectedPlan(null); setPayingPlanId(null); }}
        title="Confirmer votre achat"
      >
        {selectedPlan && (() => {
          const { total } = computePrice(selectedPlan, billingPeriod);
          const c = getColor(selectedPlan, 0);
          return (
            <div style={{ padding: 14, background: C.bg, borderRadius: 8, marginBottom: 14, textAlign: "center" }}>
              <div style={{ color: c, fontSize: 14, fontWeight: 700 }}>{selectedPlan.title}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: c, marginTop: 4 }}>
                {safeFmt(total, selectedPlan.currency)} / {LABEL[billingPeriod]}
              </div>
            </div>
          );
        })()}
        <p style={{ textAlign: "center", marginBottom: 14, color: C.textLight, fontSize: 13 }}>
          Cliquez sur « Procéder au paiement » pour continuer.
        </p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={() => { setConfirmModalVisible(false); setSelectedPlan(null); setPayingPlanId(null); }} style={styles.btnGrey}>
            Annuler
          </button>
          <button onClick={handleConfirmPurchase} style={{ ...styles.btnPrimary, background: C.orange }}>
            Procéder au paiement
          </button>
        </div>
      </FocusModal>

      <PaymentWrapper
        visible={paymentModalVisible}
        course={planToPayFor}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
        onCancel={handlePaymentCancel}
      />

      <ContactDialog
        visible={contactModalVisible}
        onClose={() => setContactModalVisible(false)}
        selectedPlan={selectedPlan}
        contactForm={contactForm}
        setContactForm={setContactForm}
        contactErrors={contactErrors}
        setContactErrors={setContactErrors}
        contactLoading={contactLoading}
        contactSuccess={contactSuccess}
        setContactSuccess={setContactSuccess}
        onSubmit={submitContact}
      />
    </div>
  );
};

/* ================================================================
   Styles — V3 White + Blue/Orange accents
   ================================================================ */
const styles = {
  container: {
    background: "white",
    padding: "44px 12px",
    fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    borderBottom: `1px solid ${C.border}`,
  },
  header: {
    textAlign: "center",
    marginBottom: 28,
  },
  title: {
    color: C.blueDark,
    fontSize: 28,
    fontWeight: 800,
    margin: "8px 0 6px",
    fontFamily: "'Outfit', sans-serif",
  },
  subtitle: {
    color: C.textLight,
    fontSize: 14,
    margin: 0,
  },

  cardsGrid: {
    display: "grid",
    gap: 20,
    maxWidth: 1400,
    margin: "0 auto",
    padding: "0 8px",
    // grid-template-columns sera défini dynamiquement
  },
  card: {
    borderRadius: 16,
    background: "white",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 480,
  },

  modalOverlay: {
    position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.4)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 2000, padding: 16, backdropFilter: "blur(3px)",
  },
  modalCard: {
    backgroundColor: "white", borderRadius: 12, padding: 24,
    width: "min(460px, 94vw)", boxShadow: "0 16px 40px rgba(0,0,0,0.15)",
  },
  modalTitle: { margin: "0 0 14px", fontSize: 17, color: "#111", fontWeight: 700 },
  contactSuccess: {
    padding: 12, background: "#f6ffed", border: "1px solid #b7eb8f",
    borderRadius: 8, color: "#389e0d", marginBottom: 12, fontSize: 13,
  },

  btnGrey: {
    padding: "9px 18px", backgroundColor: "#f0f0f0", border: "none",
    borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 500,
  },
  btnPrimary: {
    padding: "9px 20px", backgroundColor: C.blue, color: "white",
    border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700,
  },
};

export default DedicatedAccessSection;